import { inject, Injectable } from '@angular/core';
import { ContextService } from 'core/services/context.service';
import { environment } from 'environment/environment';
import { initialize, LDClient, LDContext, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { combineLatest, Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService {
  private client?: LDClient;
  private destroy$ = new Subject<void>();
  private contextService = inject(ContextService);

  constructor() {
    this.initializeForSiteAndClient();
  }

  public close(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.client?.close().then();
  }

  private initializeForSiteAndClient(): void {
    combineLatest([this.contextService.clientId, this.contextService.siteId, this.contextService.userInfo])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([clientId, siteId, userInfo]) => {
        if (!clientId || !siteId || !userInfo) {
          return;
        }
        const context: LDContext = {
          kind: 'user',
          key: userInfo.authId,
          clientId,
          siteId,
        };

        if (!this.client) {
          this.client = initialize(environment.launchDarklyClientId, context);

          this.client.on('ready', () => {
            const flags = this.client!.allFlags();
            this.contextService.featureFlags.next(flags);
          });

          this.client.on('change', (flags: Record<string, unknown>) => {
            this.populateFlags(flags);
          });
        } else {
          this.client.identify(context, undefined, (_: Error | null, flags: LDFlagSet | null) => {
            if (flags) {
              this.populateFlags(flags);
            } else {
              console.log('[LaunchDarkly] Identify callback returned no flags');
            }
          });
        }
      });
  }

  private populateFlags(flags: LDFlagSet): void {
    const existing = this.contextService.featureFlags.getValue() ?? {};
    const updated = { ...existing, ...flags };
    this.contextService.featureFlags.next(updated);
  }
}
